import React, { useMemo, useState } from 'react';
import { Button, Icon, Image, Loader } from 'semantic-ui-react';
import { format, isToday, isYesterday } from 'date-fns';
import api from '../utils/api';
import './ChangeHistory.css';

/**
 * Readable change history: who changed what, when, with each change shown as Before | After.
 * Entries come from the backend's History collection (already decoded: people by name, fields by label).
 */

export const KINDS = [
	{ key: 'edit', label: 'Edits', icon: 'pencil' },
	{ key: 'notification', label: 'Notifications', icon: 'send' },
	{ key: 'confirmation', label: 'Replies', icon: 'check circle outline' },
];

const VIA = {
	editor: 'Event editor',
	planner: 'Planner',
	services: 'Services page',
	confirmation: 'Reply',
	notification: 'Alert sent',
	blockout: 'Blockout auto-decline',
	'auto-create': 'Auto-create from template',
	'auto-rotation': 'Rotation after a decline',
	'auto-correct': 'Rotation auto-correct',
	'group-sync': 'Group change',
	'template-sync': 'Template change',
};

const COLLECTION_NOUN = { Events: 'event', EventTemplates: 'template', Groups: 'group', Members: 'profile' };

/** changes of the selected kinds (create / delete entries count as edits) */
export const visibleChanges = (entry, kinds) => (entry.changes ?? []).filter(c => kinds.includes(c.kind));
export const isEntryVisible = (entry, kinds) =>
	entry.action !== 'update' ? kinds.includes('edit') : visibleChanges(entry, kinds).length > 0;

const dayLabel = (d) => isToday(d) ? 'Today' : isYesterday(d) ? 'Yesterday' : format(d, 'EEEE, MMM d, yyyy');

export const targetLabel = (entry) => {
	if (entry.collection === 'Events') {
		const start = entry.eventStart ? new Date(entry.eventStart) : undefined;
		return `${start ? format(start, 'EEE M/d h:mm a') + ' ' : ''}${entry.title ?? 'event'}`;
	}
	return entry.title ?? COLLECTION_NOUN[entry.collection];
};

/** "edited", "replied", "created" ... from what the (visible) changes are */
function verb(entry, changes) {
	if (entry.action === 'create') return 'created';
	if (entry.action === 'delete') return 'deleted';
	const kinds = new Set(changes.map(c => c.kind));
	if (kinds.size === 1 && kinds.has('confirmation')) return 'replied';
	if (kinds.size === 1 && kinds.has('notification')) {
		const sent = changes.flatMap(c => c.after ?? []).join(' ');
		const texts = /Texted/.test(sent), emails = /Emailed/.test(sent);
		return texts && emails ? 'sent texts and emails' : emails ? 'sent emails' : 'sent texts';
	}
	return 'edited';
}

function Who({ entry }) {
	if (entry.authorName) {
		return (
			<span className='ch-who'>
				<Image avatar src={entry.authorAvatar || api.getInitialsImg(entry.authorName)} className='ch-avatar' alt='' />
				<strong>{entry.authorName}</strong>
			</span>
		);
	}
	return (
		<span className='ch-who'>
			<span className='ch-avatar ch-avatar-system'><Icon name='cog' fitted /></span>
			<strong>System</strong>
		</span>
	);
}

/** marks which items of `a` are not in `b` (multiset: duplicate names count separately) */
const missingFrom = (a, b) => {
	const rest = [...b];
	const out = new Set();
	a.forEach((x, i) => {
		const j = rest.indexOf(x);
		if (j >= 0) rest.splice(j, 1);
		else out.add(i);
	});
	return out;
};

const COLLAPSE_UNCHANGED = 6;

/** list values: removed items struck through on the left, added items highlighted on the right */
function ListDiff({ before = [], after = [] }) {
	const removed = missingFrom(before, after);
	const added = missingFrom(after, before);
	const unchanged = before.length - removed.size;
	// long lists where only a few people changed (e.g. Attending): show the changes, fold the rest
	const foldable = unchanged > COLLAPSE_UNCHANGED;
	const [showAll, setShowAll] = useState(false);
	const fold = foldable && !showAll;

	const side = (items, changed, cls, emptyText) => {
		const shown = items.map((x, i) => [x, i]).filter(([, i]) => !fold || changed.has(i));
		return (
			<>
				{items.length === 0 && <span className='ch-empty'>{emptyText}</span>}
				{shown.map(([x, i]) => <span key={i} className={`ch-chip ${changed.has(i) ? cls : ''}`}>{x}</span>)}
				{fold && <button type='button' className='ch-fold' onClick={() => setShowAll(true)}>+{unchanged} unchanged</button>}
			</>
		);
	};

	return (
		<>
			<div className='ch-cell ch-before' data-label='Before'>{side(before, removed, 'ch-removed', 'None')}</div>
			<div className='ch-cell ch-after' data-label='After'>
				{side(after, added, 'ch-added', 'None')}
				{foldable && showAll && <button type='button' className='ch-fold' onClick={() => setShowAll(false)}>Hide unchanged</button>}
			</div>
		</>
	);
}

/** replies are colored by what they mean (accepted / declined / no answer), not by before / after */
const replyClass = (text) => /^Accepted/.test(text) ? 'ch-reply-yes' : /^Declined/.test(text) ? 'ch-reply-no' : 'ch-reply-none';

function ValueDiff({ before, after, kind }) {
	const cls = (x, side) => kind === 'confirmation' ? replyClass(x) : side;
	return (
		<>
			<div className='ch-cell ch-before' data-label='Before'>
				{before?.length ? before.map((x, i) => <div key={i} className={`ch-text ${cls(x, 'ch-removed-text')}`}>{x}</div>) : <span className='ch-empty'>Not set</span>}
			</div>
			<div className='ch-cell ch-after' data-label='After'>
				{after?.length ? after.map((x, i) => <div key={i} className={`ch-text ${cls(x, 'ch-added-text')}`}>{x}</div>) : <span className='ch-empty'>Removed</span>}
			</div>
		</>
	);
}

function ChangeRow({ change, useRoles }) {
	const label = useRoles && change.roles?.length ? change.roles.join(', ') : change.label;
	const fieldNote = useRoles && change.roles?.length && change.label ? change.label : undefined;
	const kind = KINDS.find(k => k.key === change.kind);
	return (
		<div className='ch-row'>
			<div className='ch-cell ch-what'>
				<div className='ch-label'>
					{change.kind !== 'edit' && <Icon name={kind?.icon} className={`ch-kind-icon ch-kind-${change.kind}`} />}
					{label}
				</div>
				{(change.detail || fieldNote) &&
					<div className='ch-detail'>{[change.detail, fieldNote].filter(Boolean).join(' in ')}</div>}
			</div>
			{change.list ? <ListDiff before={change.before} after={change.after} /> : <ValueDiff before={change.before} after={change.after} kind={change.kind} />}
		</div>
	);
}

function EntryDetails({ entry, changes, useRoles }) {
	if (entry.action === 'delete') return <div className='ch-note'>The {COLLECTION_NOUN[entry.collection] ?? 'record'} was deleted.</div>;
	if (changes.length === 0) return <div className='ch-note'>Nothing else to show for this change.</div>;

	const sections = [];
	changes.forEach(c => {
		const name = useRoles ? (c.section === 'Details' ? 'Details' : 'Services roles') : c.section;
		let s = sections.find(x => x.name === name);
		if (!s) sections.push(s = { name, changes: [] });
		s.changes.push(c);
	});

	return (
		<div className='ch-details'>
			{sections.map(s => (
				<section key={s.name} className='ch-section'>
					<h5 className='ch-section-title'>{s.name}</h5>
					<div className='ch-grid'>
						<div className='ch-row ch-head' aria-hidden='true'>
							<div className='ch-cell'>Changed</div>
							<div className='ch-cell'>Before</div>
							<div className='ch-cell'>After</div>
						</div>
						{s.changes.map((c, i) => <ChangeRow key={i} change={c} useRoles={useRoles} />)}
					</div>
				</section>
			))}
		</div>
	);
}

const clip = (text, max = 60) => text && text.length > max ? `${text.slice(0, max - 1)}…` : text;
const plural = (n, one, many) => `${n} ${n === 1 ? one : many}`;

/** The 2nd line of a collapsed row: what changed, per kind (edits with notes, replies with ✓ / ✗, notification counts) */
function Preview({ entry, changes, useRoles }) {
	if (entry.action === 'delete' || changes.length === 0) return null;
	const nameOf = (c) => useRoles && c.roles?.length ? c.roles.join(', ') : c.label;
	const parts = [];

	// edits: changed fields; notes as old → new
	const edits = changes.filter(c => c.kind === 'edit');
	const editLabels = [...new Set(edits.map(nameOf))];
	editLabels.slice(0, 5).forEach(label => {
		// field notes (detail 'Notes') and note-like values such as a profile's Notes or an event's Description
		const notes = edits.find(c => nameOf(c) === label && !c.list && (c.detail === 'Notes' || (!c.detail && /^(Notes|Description|Event description)$/.test(c.label))));
		parts.push(
			<span key={`e-${label}`} className='ch-pv-item'>
				{label}
				{notes && <>: <span className='ch-pv-old'>{clip(notes.before?.[0]) ?? '—'}</span> → <span className='ch-pv-new'>{clip(notes.after?.[0]) ?? '—'}</span></>}
			</span>
		);
	});
	if (editLabels.length > 5) parts.push(<span key='e-more' className='ch-pv-item'>and {editLabels.length - 5} more</span>);

	// replies: ✓ accepted / ✗ declined in front of the fields
	changes.filter(c => c.kind === 'confirmation').forEach((c, i) => {
		const answer = c.after?.[0] ?? '';
		const accepted = /^Accepted/.test(answer), declined = /^Declined/.test(answer);
		const someoneElse = c.detail && c.detail !== entry.authorName;
		parts.push(
			<span key={`c-${i}`} className={`ch-pv-item ${accepted ? 'ch-pv-yes' : declined ? 'ch-pv-no' : 'ch-pv-none'}`}
				title={answer}>
				<Icon name={accepted ? 'check' : declined ? 'times' : 'circle outline'} />
				{someoneElse ? `${c.detail}: ` : ''}{nameOf(c)}
				{declined && /\(/.test(answer) && <span className='ch-pv-reason'> {answer.replace(/^Declined\s*/, '')}</span>}
			</span>
		);
	});

	// notifications: how many texts / emails, to how many people, for which fields
	const sent = changes.filter(c => c.kind === 'notification');
	if (sent.length) {
		// one message per person and send time, even when it covers several of their fields (e.g. Preaching + Attending)
		const messages = (re) => new Set(sent.filter(c => re.test(c.after?.[0] ?? '')).map(c => `${c.personId ?? c.detail}|${c.after?.[0]}`)).size;
		const texts = messages(/^Texted/);
		const emails = messages(/^Emailed/);
		const people = new Set(sent.map(c => c.personId ?? c.detail)).size;
		const fields = [...new Set(sent.map(nameOf))];
		const counts = [texts && plural(texts, 'text', 'texts'), emails && plural(emails, 'email', 'emails')].filter(Boolean).join(' + ');
		parts.push(
			<span key='n' className='ch-pv-item'>
				<Icon name='send' className='ch-kind-icon ch-kind-notification' />
				{counts} to {plural(people, 'person', 'people')} · {fields.join(', ')}
			</span>
		);
	}

	return <span className='ch-preview'>{parts}</span>;
}

function Entry({ entry, kinds, showTarget, useRoles, onOpenTarget, defaultOpen }) {
	const [open, setOpen] = useState(!!defaultOpen);
	const changes = visibleChanges(entry, kinds);
	const time = new Date(entry.time);
	const via = VIA[entry.via];
	const hasMeta = via || entry.version !== undefined || (entry.service && entry.service !== entry.title);

	return (
		<li className={`ch-entry ${open ? 'ch-open' : ''}`}>
			<button type='button' className='ch-summary' aria-expanded={open} onClick={() => setOpen(!open)}>
				<span className='ch-time'>{format(time, 'h:mm a')}</span>
				<span className='ch-main'>
					<span className='ch-headline'>
						<Who entry={entry} />
						<span className='ch-verb'>{verb(entry, changes)}</span>
						{showTarget && <span className='ch-target'>{COLLECTION_NOUN[entry.collection]}{' '}<strong>{targetLabel(entry)}</strong></span>}
					</span>
					<Preview entry={entry} changes={changes} useRoles={useRoles} />
				</span>
				<Icon name={open ? 'chevron up' : 'chevron down'} className='ch-chevron' />
			</button>
			{open &&
				<div className='ch-body'>
					{hasMeta &&
						<div className='ch-meta'>
							{via && <span className='ch-tag'>{via}</span>}
							{entry.version !== undefined && <span className='ch-tag ch-tag-quiet'>Version {entry.version}</span>}
							{entry.service && entry.service !== entry.title && <span className='ch-tag ch-tag-quiet'>{entry.service}</span>}
						</div>}
					<EntryDetails entry={entry} changes={changes} useRoles={useRoles} />
					{onOpenTarget && entry.action !== 'delete' &&
						<Button basic compact size='small' className='ch-open-target' onClick={() => onOpenTarget(entry)}>
							Open {COLLECTION_NOUN[entry.collection]}
						</Button>}
				</div>}
		</li>
	);
}

/** The Edits / Notifications / Replies toggles */
export function KindToggles({ kinds, onChange, counts, only }) {
	const options = KINDS.filter(k => !only || only.includes(k.key));
	return (
		<div className='ch-toggles' role='group' aria-label='Show changes'>
			{options.map(k => {
				const active = kinds.includes(k.key);
				return (
					<button
						key={k.key}
						type='button'
						aria-pressed={active}
						className={`ch-toggle ch-kind-${k.key} ${active ? 'ch-toggle-on' : ''}`}
						onClick={() => onChange(active ? kinds.filter(x => x !== k.key) : [...kinds, k.key])}
					>
						<Icon name={active ? 'check square' : 'square outline'} />
						{k.label}{counts && counts[k.key] !== undefined ? ` (${counts[k.key]})` : ''}
					</button>
				);
			})}
		</div>
	);
}

/**
 * props: entries, kinds (selected kind keys), showTarget (dashboard: say which record), useRoles (Services: label
 * changes by role), onOpenTarget(entry), loading, error, emptyText, hasMore + onLoadMore, loadingMore
 */
export default function ChangeHistory(props) {
	const { entries = [], kinds, showTarget, useRoles, onOpenTarget, loading, error, emptyText, hasMore, onLoadMore, loadingMore } = props;

	const days = useMemo(() => {
		const out = [];
		entries.filter(e => isEntryVisible(e, kinds)).forEach(e => {
			const d = new Date(e.time);
			const key = format(d, 'yyyy-MM-dd');
			let day = out[out.length - 1];
			if (!day || day.key !== key) out.push(day = { key, label: dayLabel(d), entries: [] });
			day.entries.push(e);
		});
		return out;
	}, [entries, kinds]);

	if (loading) return <div className='ch-status'><Loader active inline size='small' /> Loading history…</div>;
	if (error) return <div className='ch-status ch-error'><Icon name='warning circle' />Couldn't load the history: {String(error)}</div>;

	return (
		<div className='ch'>
			{days.length === 0 && <div className='ch-status'>{emptyText ?? 'No changes yet.'}</div>}
			{days.map(day => (
				<div key={day.key} className='ch-day'>
					<h4 className='ch-day-title'>{day.label}</h4>
					<ul className='ch-list'>
						{day.entries.map(e => <Entry key={e.id} entry={e} kinds={kinds} showTarget={showTarget} useRoles={useRoles} onOpenTarget={onOpenTarget} />)}
					</ul>
				</div>
			))}
			{hasMore &&
				<div className='ch-more'>
					<Button basic compact loading={loadingMore} disabled={loadingMore} onClick={onLoadMore}>Show older changes</Button>
				</div>}
		</div>
	);
}
